import { Skeleton } from '@mantine/core';
import type { ContainerResponse } from '@xver/apis/v1/kufer/containers/model';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface ContainerSummaryTilesProps {
    containers: ContainerResponse[] | undefined;
    isPending: boolean;
}

interface TileProps {
    label: string;
    value: number;
    suffix: string;
    isPending: boolean;
    cornerClass?: string;
    valueClass?: string;
}

function Tile({ label, value, suffix, isPending, cornerClass = '', valueClass = '' }: TileProps) {
    return (
        <div
            className={`w-full h-auto p-4 flex flex-col justify-start items-start gap-1 bg-surface-1 ${cornerClass}`}
        >
            <h2 className="text-xs text-fg/68 sm:text-base">{label}</h2>
            {!isPending ? (
                <div className="inline-flex items-center gap-1">
                    <span className={`text-xl font-bold sm:text-2xl ${valueClass}`}>{value}</span>
                    <span className="text-xs text-fg/68 sm:text-base">{suffix}</span>
                </div>
            ) : (
                <Skeleton height={31} width={'100%'} radius="sm" />
            )}
        </div>
    );
}

export default function ContainerSummaryTiles({
    containers,
    isPending,
}: ContainerSummaryTilesProps) {
    const { t } = useTranslation('containerEngine');

    const counts = useMemo(() => {
        const list = containers ?? [];
        return {
            total: list.length,
            running: list.filter((c) => c.status === 'running').length,
            building: list.filter(
                (c) => c.status === 'building' || c.status === 'pending' || c.status === 'deploying'
            ).length,
            failed: list.filter((c) => c.status === 'failed').length,
        };
    }, [containers]);

    const suffix = t('summary.totalSuffix', 'containers');

    return (
        <div className="w-full grid grid-cols-1 lg:grid-cols-4 border-[0.5px] border-line rounded-sm">
            <Tile
                label={t('summary.total', 'Total')}
                value={counts.total}
                suffix={suffix}
                isPending={isPending}
                cornerClass="border-b-[0.5px] border-line lg:border-b-0 rounded-none lg:rounded-l-sm"
            />
            <Tile
                label={t('summary.running', 'Running')}
                value={counts.running}
                suffix={suffix}
                isPending={isPending}
                cornerClass="border-b-[0.5px] border-l-0 lg:border-l-[0.5px] lg:border-b-0 border-line"
                valueClass="text-green-400 light:text-green-800"
            />
            <Tile
                label={t('summary.building', 'Building')}
                value={counts.building}
                suffix={suffix}
                isPending={isPending}
                cornerClass="border-b-[0.5px] border-l-0 lg:border-l-[0.5px] lg:border-b-0 border-line"
                valueClass="text-amber-400 light:text-amber-800"
            />
            <Tile
                label={t('summary.failed', 'Failed')}
                value={counts.failed}
                suffix={suffix}
                isPending={isPending}
                cornerClass="border-l-0 lg:border-l-[0.5px] border-line rounded-r-sm"
                valueClass={counts.failed > 0 ? 'text-red-400 light:text-red-700' : ''}
            />
        </div>
    );
}
